'use client';

import { AlertCircle, Button, Mail } from '@sprd/sprd-component-kit/v2';
import React, { useActionState, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  type NewsletterSignUpResult,
  newsletterSignUpReducer,
} from '@/app/actions/newsletterSignUp';
import { Locale } from '@/utility/locale';

export type NewsletterSignUpFormProps = {
  locale: Locale;
  formHeader: React.ReactNode;
};

export default function NewsletterSignUpForm({ locale, formHeader }: NewsletterSignUpFormProps) {
  const [email, setEmail] = useState('');
  const t = useTranslations();
  const [result, action, isPending] = useActionState<NewsletterSignUpResult | null, FormData>(
    newsletterSignUpReducer,
    null
  );

  const status: 'idle' | 'loading' | 'success' | 'error' = isPending
    ? 'loading'
    : result
      ? result.ok
        ? 'success'
        : 'error'
      : 'idle';

  return (
    <div>
      {status === 'success' ? (
        <div>
          <h2 className="text-2xl leading-tight font-extrabold tracking-tight uppercase sm:text-3xl lg:text-4xl">
            {t('newsletter.successTitle')}
          </h2>
          <p className="mt-4 max-w-prose">{t('newsletter.successDescription')}</p>
        </div>
      ) : (
        <>
          {formHeader}
          <form action={action} className="mt-6 w-full gap-6 md:flex md:max-w-2xl xl:max-w-3xl">
            {/* Canonical locale; the server action derives the wire format. */}
            <input type="hidden" name="locale" value={locale} />
            <div className="relative flex w-full flex-col md:flex-1 md:flex-row">
              <div className="flex w-full flex-col">
                <div className="relative">
                  <label htmlFor="newsletter-email" className="sr-only">
                    {t('newsletter.emailPlaceholder')}
                  </label>
                  <input
                    id="newsletter-email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 w-full border border-neutral-700 bg-white px-2 py-3 pr-10 text-sm text-black placeholder:text-sm focus:border-neutral-700 focus:outline-none"
                    placeholder={t('newsletter.emailPlaceholder')}
                  />
                  <span className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-2xl text-black">
                    <Mail />
                  </span>
                </div>
                {status === 'error' && (
                  <p className="mt-3 flex items-start gap-1 text-sm md:items-center">
                    <span className="text-lg md:text-2xl">
                      <AlertCircle />
                    </span>
                    {t('newsletter.errorMessage')}
                  </p>
                )}
              </div>
            </div>
            <Button
              type="submit"
              size="l"
              disabled={isPending}
              variant="ghost-invert"
              className="mt-6 w-full md:mt-0 md:w-auto"
            >
              {t('newsletter.subscribeNow')}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
